import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { API } from '../api';
import PageHeader from '../ui/PageHeader';
import FilterBar from '../ui/FilterBar';
import useUrlFilters from '../ui/useUrlFilters';
import useAsync from '../ui/useAsync';
import DataTable from '../ui/DataTable';
import ListPage, { useListQuery } from '../ui/ListPage';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { formatCount } from '../ui/format';
import EditOwnershipPanel from './EditOwnershipPanel';

// URL keys are the legacy ones; "empty" is the API's value for "not set".
const FILTER_DEFAULTS = { kw: '', team: '', project: '', admin: '', region: '' };
const NO_OPTIONS = { teams: [], projects: [], admins: [], regions: [] };

const valueOptions = (values) => [
  { value: '', label: 'All' },
  { value: 'empty', label: 'Not set' },
  ...values.map((v) => ({ value: v, label: v })),
];
const filtersFor = (options) => [
  { key: 'kw', type: 'search', label: 'Host', placeholder: 'Hostname', mono: true, width: 240 },
  { key: 'team', type: 'select', label: 'Team', options: valueOptions(options.teams) },
  { key: 'project', type: 'select', label: 'Project', options: valueOptions(options.projects) },
  { key: 'admin', type: 'select', label: 'Admin', options: valueOptions(options.admins) },
  { key: 'region', type: 'select', label: 'Region', options: valueOptions(options.regions) },
];

const isBlank = (value) => !value || !String(value).trim();
// No team, project or admin: the hosts the legacy page highlighted.
const isUnassigned = (row) => isBlank(row.team_flow) && isBlank(row.team_project) && isBlank(row.host_admin);

const dash = (value) => (isBlank(value) ? <span className="text-graphite">—</span> : value);

const columns = (onEdit) => [
  {
    key: 'hostname',
    header: 'Host',
    hideable: false,
    render: (r) => (
      <span className="inline-flex items-center gap-2 max-w-full">
        <EntityLink kind="host" id={r.id} name={r.hostname} />
        {isUnassigned(r) && <Badge tone="med" title="No team, project or admin">Unassigned</Badge>}
      </span>
    ),
  },
  { key: 'team', header: 'Team', render: (r) => dash(r.team_flow) },
  { key: 'project', header: 'Project', maxWidth: 220, fullText: (r) => r.team_project, render: (r) => dash(r.team_project) },
  { key: 'admin', header: 'Admin', maxWidth: 220, fullText: (r) => r.host_admin, render: (r) => dash(r.host_admin) },
  { key: 'region', header: 'Region', render: (r) => dash(r.region) },
  {
    key: 'edit',
    header: <span className="sr-only">Edit</span>,
    width: 72,
    render: (r) => (
      <Button size="sm" variant="ghost" className="text-signal" onClick={() => onEdit(r.hostname)} aria-label={`Edit ownership of ${r.hostname}`}>
        Edit
      </Button>
    ),
  },
];

export default function HostOwnership() {
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();
  const [filters, setFilters] = useUrlFilters(FILTER_DEFAULTS);
  const options = useAsync(() => API.GET_OWNERSHIP_OPTIONS(), 'ownership-options');
  const ownershipOptions = { ...NO_OPTIONS, ...(options.data || {}) };
  const filterDefs = filtersFor(ownershipOptions);

  // Selection is by hostname (what the update takes) and survives paging and filter changes,
  // so hosts from several searches can be edited together, as on the legacy page.
  const [selected, setSelected] = useState([]);
  const [allMatchingFor, setAllMatchingFor] = useState(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [panel, setPanel] = useState(null);
  const seen = useRef({});
  const remember = (rows) => rows.forEach((r) => { seen.current[r.hostname] = r; });

  const key = JSON.stringify(filters);
  const apiFilters = { kw: filters.kw, team: filters.team, project: filters.project, admin: filters.admin, region: filters.region };
  const fetchPage = useCallback(
    (paging) => API.GET_HOST_OWNERSHIPS({ ...apiFilters, ...paging }),
    [key], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const query = useListQuery(fetchPage, key, 'host_ownerships');
  const { rows, total, retry } = query;
  remember(rows);

  // ?edit=<hostname> (from the host and ticket pages) opens the panel for that host.
  const editName = new URLSearchParams(location.search).get('edit');
  useEffect(() => {
    if (!editName) return undefined;
    let live = true;
    API.GET_HOST_OWNERSHIPS({ kw: editName, offset: 0, limit: 50 }).then(
      (res) => {
        const row = (res?.data || []).find((r) => r.hostname === editName);
        if (!live || !row) return;
        remember([row]);
        setPanel({ hosts: [row.hostname] });
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [editName]); // eslint-disable-line react-hooks/exhaustive-deps

  const closePanel = () => {
    setPanel(null);
    if (!editName) return;
    const params = new URLSearchParams(location.search);
    params.delete('edit');
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  const changeSelection = (next) => {
    setSelected(next);
    setAllMatchingFor(null);
  };

  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const res = await API.GET_HOST_OWNERSHIPS({ ...apiFilters, offset: 0, limit: -1 });
      const matching = res?.data || [];
      remember(matching);
      setSelected((prev) => [...new Set([...prev, ...matching.map((r) => r.hostname)])]);
      setAllMatchingFor(key);
    } catch (error) {
      toast.error(`The matching hosts could not be selected: ${error.message}`);
    } finally {
      setSelectingAll(false);
    }
  };

  const saved = ({ hostnames }) => {
    if (hostnames.length > 1) changeSelection([]);
    hostnames.forEach((h) => { delete seen.current[h]; });
    retry();
    options.retry();
  };

  const selectionBar = (
    <>
      <span className="font-semibold tabular-nums">{formatCount(selected.length)} selected</span>
      <span className="mx-1 h-4 border-l border-rule" aria-hidden="true" />
      <Button size="sm" variant="primary" onClick={() => setPanel({ hosts: selected })}>Edit ownership</Button>
      {total > rows.length && (allMatchingFor === key ? (
        <span className="ml-1 text-small text-graphite">All {formatCount(total)} matching hosts are selected.</span>
      ) : (
        <Button size="sm" variant="ghost" className="text-signal" onClick={selectAllMatching} disabled={selectingAll}>
          {selectingAll ? 'Selecting…' : `Select all ${formatCount(total)} matching`}
        </Button>
      ))}
      <Button size="sm" variant="ghost" className="ml-auto" onClick={() => changeSelection([])}>
        Clear selection
      </Button>
    </>
  );

  const filtered = filterDefs.some((f) => filters[f.key] !== FILTER_DEFAULTS[f.key]);
  const empty = filtered ? (
    <EmptyState title="No hosts match these filters" action={<Button onClick={() => setFilters(FILTER_DEFAULTS)}>Clear filters</Button>}>
      Host matches part of the name. Pick &ldquo;Not set&rdquo; to find hosts with an empty field.
    </EmptyState>
  ) : (
    <EmptyState title="No active hosts">Hosts appear here once their agents report an inventory.</EmptyState>
  );

  return (
    <ListPage>
      <PageHeader
        title="Host ownership"
        description="Who is responsible for each active host. Tickets go to the host admin."
        actions={<Button onClick={() => setPanel({ hosts: null })}>Edit by host name</Button>}
      />
      <FilterBar className="mb-3" filters={filterDefs} values={filters} onChange={setFilters} />
      <DataTable
        className="flex-1"
        label="Host ownership"
        columns={columns((hostname) => setPanel({ hosts: [hostname] }))}
        rows={rows}
        rowKey={(r) => r.hostname}
        rowLabel={(r) => r.hostname}
        selection={{ selected, onChange: changeSelection }}
        selectionBar={selectionBar}
        loading={query.loading}
        error={query.error}
        errorTitle="Host ownership could not be loaded"
        onRetry={retry}
        empty={empty}
        pagination={query.pagination}
      />
      <EditOwnershipPanel
        open={Boolean(panel)}
        hosts={panel?.hosts ?? null}
        rows={seen.current}
        options={ownershipOptions}
        onClose={closePanel}
        onSaved={saved}
      />
    </ListPage>
  );
}
